import { useEffect, useState } from 'react';
import type { CardFace } from '@/components/feed/FeedCard';

/**
 * Tiny persisted user preference for the Feed: which card face shows by
 * default — stats first or photo first. Sniffies-style toggle, both valid.
 */

const STORAGE_KEY = 'hottr.app.feedPrefs';

interface FeedPreferences {
  cardFace: CardFace;
}

// Image-led by default — the brief is explicit. Stats remain reachable via flip,
// but the cinematic photo face is what the user sees first.
const DEFAULT: FeedPreferences = { cardFace: 'photo' };

const read = (): FeedPreferences => {
  if (typeof window === 'undefined') return DEFAULT;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT;
    const parsed = JSON.parse(raw);
    return {
      cardFace: parsed.cardFace === 'photo' ? 'photo' : 'stats',
    };
  } catch {
    return DEFAULT;
  }
};

const write = (prefs: FeedPreferences) => {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
};

export const useFeedPreferences = () => {
  const [prefs, setPrefs] = useState<FeedPreferences>(DEFAULT);

  useEffect(() => {
    setPrefs(read());
  }, []);

  const setCardFace = (cardFace: CardFace) => {
    const next = { ...prefs, cardFace };
    write(next);
    setPrefs(next);
  };

  return { ...prefs, setCardFace };
};
